import { hotelCampaignRegistrationStatuses } from 'main/hotelStatuses'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getHealthCheckInvoices } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import Toggle from '../../../components/ReusableComponents/Toggle/index'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import styles from './Billing.module.scss'
import InvoicesRow from './Row'
import InvoicesTitleRow from './TitleRow'
import TotalRow from './TotalRow'

const HealthCheckPerformance = () => {
  const dispatch = useDispatch()

  const [results, setResults] = useState([])
  const [totalBarData, setTotalBarData] = useState()

  const [filterState, filterDispatch] = useHealthCheckFilters('performance')

  const [highlightNotSent, setHighlightNotSent] = useState(true)
  const [highlightPaymentPending, setHighlightPaymentPending] = useState(true)

  useEffect(() => {
    dispatch(getHealthCheckInvoices())
  }, [dispatch])

  const isLoading = useSelector(s => s.healthCheck.invoices.isLoading)
  const hotelData = useSelector(s => s.healthCheck.invoices.hotelData)
  const months = useSelector(s => s.healthCheck.invoices.months)
  const allhotelGroups = useSelector(s => s.healthCheck.invoices.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.invoices.hotelChains)

  const [sortByIndex, setSortByIndex] = useState(1)
  const [sortDecreasing, setSortDecreasing] = useState(true)

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  useEffect(() => {
    function getFilteredResults(hotelData) {
      const { searchString, chains, brands, hotelGroups, subsidiaryStatus, csmIds } =
        filterState
      return hotelData
        .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
        .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
        .filter(h => (chains === 'all' ? true : h.chainCode === chains))
        .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
        .filter(h => {
          if (subsidiaryStatus.length === 0) {
            return true
          } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
            return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
          } else {
            return subsidiaryStatus.includes(h.subsidiaryStatus)
          }
        })
        .filter(h => {
          if (csmIds.length === 0) {
            return true
          } else {
            return csmIds.includes(`${h.csmUserId}`)
          }
        })
    }

    function getSortedResults(hotelData, sortBy, sortDecreasing) {
      const titleRowNames = ['displayId', 'shortName', ...months]
      const sortByProp = titleRowNames[sortBy]
      const sortedData = hotelData.sort((a, b) => {
        let aVal = a[sortByProp]
        let bVal = b[sortByProp]

        if (sortBy > 1) {
          aVal = a.items?.[sortByProp]?.totalAmountDue || 0
          bVal = b.items?.[sortByProp]?.totalAmountDue || 0
        }

        if (typeof aVal === 'string') {
          if (aVal === bVal) {
            return 0
          }
          return aVal?.toUpperCase() > bVal?.toUpperCase()
        } else if (typeof aVal === 'boolean') {
          return Number(bVal) - Number(aVal)
        } else {
          return bVal - aVal
        }
      })
      return sortDecreasing ? sortedData : sortedData.reverse()
    }

    function getResults() {
      const results = getSortedResults(
        getFilteredResults(hotelData),
        sortByIndex,
        sortDecreasing
      )

      const totalBarData = months.reduce(
        (carried, currentMonth) => {
          return {
            ...carried,
            [currentMonth]: results.reduce(
              (prev, curr) => {
                return {
                  chargedBookings:
                    prev.chargedBookings +
                    (curr?.items?.[currentMonth]?.bookingCountToCharge || 0),
                  nonChargedBookings:
                    prev.nonChargedBookings +
                    (curr?.items?.[currentMonth]?.bookingCountNotCharged || 0),
                  transactionVolume:
                    prev.transactionVolume +
                    (curr?.items?.[currentMonth]?.totalBookingValueToCharge || 0),
                  total: prev.total + (curr?.items?.[currentMonth]?.totalAmountDue || 0),
                  count:
                    prev.count +
                    (curr?.items?.[currentMonth]?.totalAmountDue >= 0 ? 1 : 0)
                }
              },
              {
                total: 0,
                count: 0,
                chargedBookings: 0,
                nonChargedBookings: 0,
                transactionVolume: 0
              }
            )
          }
        },
        months.reduce((prev, curr) => ({ ...prev, [curr]: { total: 0, count: 0 } }), {})
      )

      setTotalBarData({ data: totalBarData, count: results.length })
      setResults(results)
    }
    getResults()
  }, [filterState, hotelData, months, sortByIndex, sortDecreasing])

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Hotel health check" width={1180} isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
      </Tab.Parent>
      <HealthCheckFilters
        state={filterState}
        dispatch={filterDispatch}
        allhotelGroups={allhotelGroups}
        allhotelChains={allhotelChains}
        showSubsidiaryStatus={true}
        showCsmFilter
      >
        <div className={styles.highlightControls}>
          <span className={styles.highlightControlsTitle}>Highlight:</span>
          <Toggle
            className={styles.toggle}
            checked={highlightNotSent}
            handleChange={e => setHighlightNotSent(e.target.checked)}
            label="Not sent"
          />
          <Toggle
            className={styles.toggle}
            checked={highlightPaymentPending}
            handleChange={e => setHighlightPaymentPending(e.target.checked)}
            label="Payment pending"
          />
        </div>
      </HealthCheckFilters>

      <div className={styles.tableContainer}>
        {isLoading ? (
          <LoadingIndicator marginTop={150} />
        ) : (
          <Table
            widths={[40, 265, 125, 125, 125, 125, 125, 125, 125]}
            handleTitleCellClick={handleTitleCellClick}
            sortIndex={sortByIndex}
            sortDirection={sortDecreasing ? 'down' : 'up'}
          >
            <InvoicesTitleRow months={months} />

            {results.map((h, i) => (
              <InvoicesRow
                {...h}
                index={i + 1}
                months={months}
                highlightPaymentPending={highlightPaymentPending}
                highlightNotSent={highlightNotSent}
              />
            ))}
            <TotalRow
              months={months}
              data={totalBarData?.data}
              count={totalBarData?.count}
            />
          </Table>
        )}
      </div>
    </div>
  )
}

export default HealthCheckPerformance
